import { MouseEventHandler, ReactElement } from "react";

type Props = {
  id?: string;
  type?: "button" | "submit" | "reset";
  className?: string;
  status?: "default" | "loading" | "disabled" | "destructive";
  size?: "tiny" | "small" | "normal" | "large";
  variant?: "primary" | "secondary" | "outline" | "plain" | "frackfrack";
  loadingText?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;

  children: ReactElement | string;
};

const defaultStyle = "pointer-cursor";
const loadingStyle = "pointer-events-none cursor-progress";
const disabledStyle = "pointer-events-none cursor-not-allowed";
const styles = {
  baseStyle: "w-full justify-center font-medium rounded-md caret-transparent",
  primary: {
    default: `${defaultStyle} text-white bg-brand-primary hover:bg-brand-primary-hover`,
    destructive: `${defaultStyle} text-white bg-red-600 hover:bg-red-700`,
    loading: `${loadingStyle} text-white bg-gray-300`,
    spinner: {
      color: "text-white",
      bgColor: "fill-gray-300",
    },
    disabled: `${disabledStyle} text-white bg-gray-300`,
  },
  secondary: {
    default: `${defaultStyle} text-white bg-brand-secondary hover:bg-brand-secondary-hover`,
    destructive: `${defaultStyle} text-white bg-red-600 hover:bg-red-700`,
    loading: `${loadingStyle} text-white bg-gray-300`,
    spinner: {
      color: "text-white",
      bgColor: "fill-gray-300",
    },
    disabled: `${disabledStyle} text-white bg-gray-300`,
  },
  outline: {
    default: `${defaultStyle} text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 drop-shadow-md`,
    destructive: `${defaultStyle} text-red-600 bg-white border border-red-600 hover:bg-red-100`,
    loading: `${loadingStyle} text-gray-400 bg-white border border-gray-400 drop-shadow-md`,
    spinner: {
      color: "text-gray-400",
      bgColor: "fill-white",
    },
    disabled: `${disabledStyle} text-gray-400 bg-white border border-gray-400 drop-shadow-md`,
  },
  plain: {
    default: `${defaultStyle} text-gray-600 bg-white hover:bg-gray-100`,
    destructive: `${defaultStyle} text-red-600 hover:bg-red-100`,
    loading: `${loadingStyle} text-gray-400 bg-white`,
    spinner: {
      color: "text-gray-400",
      bgColor: "fill-white",
    },
    disabled: `${disabledStyle} text-gray-400 bg-white`,
  },
  frackfrack: {
    default: `${defaultStyle} text-white bg-[#00008B] hover:bg-[#00006B]`,
    destructive: `${defaultStyle} text-white bg-red-600 hover:bg-red-700`,
    loading: `${loadingStyle} text-white bg-gray-300`,
    spinner: {
      color: "text-white",
      bgColor: "fill-gray-300",
    },
    disabled: `${disabledStyle} text-white bg-gray-300`,
  },
  size: {
    tiny: "text-xs py-1 px-2",
    small: "text-sm py-1.5 px-3",
    normal: "text-sm py-2.5 px-4",
    large: "text-sm py-3 px-4",
  },
};

export const PrimeButton = ({
  children,
  id,
  className,
  onClick,
  type = "button",
  size = "normal",
  variant = "primary",
  status = "default",
  loadingText = "Loading...",
}: Props): ReactElement => {
  function getButtonContent(): ReactElement | string {
    // If children is ReactNode, return it as is
    if (typeof children !== "string") return children as ReactElement;
    // If not `loading`, return string as is
    if (status !== "loading") return children as string;
    // If `loading`, return loading spinner
    return (
      <LoadingSpinner
        text={loadingText}
        spinnerColor={styles[variant].spinner.color}
        bgColor={styles[variant].spinner.bgColor}
      />
    );
  }

  const disabled = ["loading", "disabled"].includes(status);
  const stylingClassName = getPrimeButtonStyle({
    className,
    size,
    variant,
    status,
  });

  return (
    <button
      id={id}
      type={type}
      className={stylingClassName}
      disabled={disabled}
      onClick={onClick}
    >
      {getButtonContent()}
    </button>
  );
};

export const getPrimeButtonStyle = (
  {
    className,
    size = "normal",
    variant = "primary",
    status = "default",
  }: Partial<Props> = {
    size: "normal",
    variant: "primary",
    status: "default",
  }
): string => {
  const disabled = ["loading", "disabled"].includes(status);

  const baseStyle = styles.baseStyle;
  const buttonSize = styles.size[size];
  const buttonStyle = styles[variant][status];

  return `${baseStyle} ${buttonSize} ${buttonStyle} ${className}`;
};

export const LoadingSpinner = ({
  text,
  spinnerColor,
  bgColor,
}: {
  text: string;
  spinnerColor: string;
  bgColor: string;
}) => {
  const svgStyleClassname = `mr-2 h-5 w-5 animate-spin ${spinnerColor} ${bgColor}`;

  return (
    <div className="flex justify-center">
      <svg
        role="status"
        className={svgStyleClassname}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      {text}
    </div>
  );
};
